import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { StepCard } from "./component/step-card";
import { SplitShipmentDiscountForm } from "./split-shipment-discount-form";

interface SplitShipmentShippingCardProps {
  teamId: string;
}

export const SplitShipmentShippingCard = memo(
  function SplitShipmentShippingCard({
    teamId,
  }: SplitShipmentShippingCardProps) {
    const [open, setOpen] = useState(false);
    const query = useRpcQuery(
      RedoAdminRpcClientContext,
      "getSplitShipmentDiscount",
      { teamId },
    );
    const locations = useRpcQuery(
      RedoAdminRpcClientContext,
      "getSplitShipmentLocationGroups",
      { teamId },
      {
        enabled: open && query.data?.available === true,
        refetchOnWindowFocus: false,
      },
    );
    const reload = () => {
      void query.refetch();
      void locations.refetch();
    };
    const settings = query.data?.settings;
    const count = settings?.locationGroups?.length ?? 0;
    const description = settings?.enabled
      ? `${settings.percentage}% discount · ${count} location group${count === 1 ? "" : "s"}`
      : count
        ? "Off · Shipping names imported"
        : "Discount Redo shipping when an order ships separately from the merchant.";

    return (
      <StepCard
        description={description}
        fullWidthBody
        IconComponent={TruckIcon}
        onToggle={() => setOpen(!open)}
        open={open}
        title="Split-shipment shipping"
      >
        {query.isPending ? (
          <Flex p="2xl">
            <Text fontSize="xs" textColor="secondary">
              Loading shipping settings…
            </Text>
          </Flex>
        ) : query.error ? (
          <Flex dir="column" gap="md" p="2xl">
            <RedoAlert
              color="error"
              description={query.error.message}
              title="Could not load shipping settings"
            />
            <RedoButton
              hierarchy="secondary"
              onClick={() => void query.refetch()}
              size="sm"
              text="Retry"
            />
          </Flex>
        ) : !query.data?.available ? (
          <Flex p="2xl">
            <Text fontSize="xs" textColor="secondary">
              Connect Shopify to configure split-shipment shipping.
            </Text>
          </Flex>
        ) : settings ? (
          <SplitShipmentDiscountForm
            initial={settings}
            key={JSON.stringify(settings)}
            loadingLocations={locations.isFetching}
            locations={locations.data}
            locationsError={locations.error?.message}
            onReload={() => void locations.refetch()}
            onSaved={reload}
            teamId={teamId}
          />
        ) : null}
      </StepCard>
    );
  },
);
